// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { CartLineCardCondensedStyles } from "@insite/content-library/Components/CartLineCardCondensed";
import { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader from "@pacific/App/Components/Cart/LinesHeader";
import cartPageStyles from "@pacific/App/SharedStyle/CartPageStyles";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@pacific/Overrides/Components/CartLineCardExpanded";
import ProductPrice from "@pacific/Overrides/Components/ProductPrice";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable: boolean;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    extendedStyles?: CartLinesListStyles;
    showInventoryAvailability?: boolean;
    showAddToList?: boolean;
}

export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
    dataTable?: {
        header?: DataTableHeaderPresentationProps;
        manufacturerItem?: TypographyPresentationProps;
    };
}

export const orderLinesListStyles: CartLinesListStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    dataTable: {
        header: {
            typographyProps: {
                size: 14,
                weight: "bold",
                fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
                css: css`
                    letter-spacing: 0;
                    line-height: 19px;
                `,
            },
        },
        manufacturerItem: {
            size: 12,
            weight: "bold",
            fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
            css: css`
                letter-spacing: 0;
                line-height: 19px;
            `,
        },
    },
    wrapper: {
        css: css`
            ${({ isCondensed }: any) => {
                if (isCondensed) {
                    return css`
                        overflow-x: auto;
                        @media print {
                            display: block;
                            break-inside: avoid;
                        }
                    `;
                }
            }}
        `,
    },
};

const CartLinesList: FC<OwnProps> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    showInventoryAvailability,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    showAddToList,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    // <XNG-Changes>
    const { enableVat, vatPriceDisplay } = useAppSelector(state => ({
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    }));
    // </XNG-Changes>

    if (!cart.cartLines) {
        return null;
    }

    const cartLines =
        cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;

    // XNG-Changes: remove condensed
    const CartLineComponent = CartLineCardExpanded;
    // <XNG-Changes>
    if (isCondensed) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`} {...{ isCondensed }}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader
                            css={css`
                                width: 280px;
                            `}
                            title={translate("Item Description")}
                            {...styles.dataTable?.header}
                        >
                            {translate("Item Description")}
                        </DataTableHeader>
                        <DataTableHeader tight title={translate("Item #")} {...styles.dataTable?.header}>
                            {translate("Item #")}
                        </DataTableHeader>
                        <DataTableHeader tight title={translate("Price/Unit")} {...styles.dataTable?.header}>
                            {translate("Price/Unit")}
                        </DataTableHeader>
                        <DataTableHeader tight title={translate("QTY")} {...styles.dataTable?.header}>
                            {translate("QTY")}
                        </DataTableHeader>
                        <DataTableHeader tight title={translate("Total")} {...styles.dataTable?.header}>
                            {translate("Total")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {cartLines!.map(cartLine => (
                            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                                <DataTableRow key={cartLine.id} className="product-row">
                                    <DataTableCell
                                        css={css`
                                            width: 280px;
                                        `}
                                    >
                                        <ProductDescription
                                            product={cartLine}
                                            extendedStyles={cartPageStyles.cartLine?.productDescription}
                                        />
                                    </DataTableCell>
                                    <DataTableCell tight>
                                        <Typography {...styles.dataTable?.manufacturerItem}>
                                            {cartLine.manufacturerItem || cartLine.erpNumber}
                                        </Typography>
                                    </DataTableCell>
                                    <DataTableCell tight>
                                        <ProductPrice
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={false}
                                            showLabel={true}
                                            showSavingsAmount={false}
                                            showSavingsPercent={false}
                                            showInvalidPriceMessage={false}
                                            extendedStyles={cartPageStyles.cartLine?.productPrice}
                                        />
                                    </DataTableCell>
                                    <DataTableCell tight>
                                        <CartLineQuantity
                                            cart={cart}
                                            editable={editable}
                                            extendedStyles={cartPageStyles.cartLine?.quantity}
                                            {...{ showLabel: false }}
                                        />
                                    </DataTableCell>
                                    <DataTableCell tight>
                                        {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && (
                                            <SmallHeadingAndText
                                                extendedStyles={cartPageStyles.cartLine?.totalSmallHeadingAndTextTable}
                                                data-test-selector="cartline_extendedUnitNetPrice"
                                                heading={translate("Total")}
                                                text={
                                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                                }
                                            />
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            </CartLineContext.Provider>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        );
    }
    // </XNG-Changes>
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            {/* <CartLinesListHeader
                productsCount={cartLines.length}
                isCondensed={isCondensed}
                hideCondensedSelector={hideCondensedSelector}
                onChangeIsCondensed={onChangeIsCondensed}
                extendedStyles={styles.header}
            /> */}
            <LinesHeader rightTitles={["Price/Unit", "QTY", "Total"]} />
            {cartLines!.map(cartLine => {
                const cartLinePromotions = promotions?.filter(promotion => promotion.orderLineId === cartLine.id);
                return (
                    <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                        <CartLineComponent
                            cart={cart}
                            promotions={cartLinePromotions}
                            editable={editable}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            showInvalidPriceMessage={true}
                            showAddToList={showAddToList}
                            showInventoryAvailability={showInventoryAvailability}
                            extendedStyles={isCondensed ? styles.cardCondensed : styles.cardExpanded}
                        />
                    </CartLineContext.Provider>
                );
            })}
        </StyledWrapper>
    );
};

export default CartLinesList;
